/**
 * Finding a name in a list that is half Urdu and half Latin.
 *
 * The customer book grew over years. Some names were typed in Urdu, some in
 * English, and the same person may appear either way in the shopkeeper memory.
 * A plain substring match fails constantly, for two separate reasons:
 *
 *   1. Urdu has several shapes for the same sound, and which one lands in the
 *      text depends on the keyboard - Arabic ي against Urdu ی, ك against ک, the
 *      three forms of he. A name typed on one tablet will not match the same
 *      name typed on another.
 *
 *   2. Somebody looking for محمد اسلم will type "aslam", and somebody looking
 *      for "Ammad Traders" will type عماد.
 *
 * So there are two passes. searchKey fixes the first problem by collapsing
 * keyboard variants within one script. nameSkeleton fixes the second by
 * reducing either script to bare consonants, which is what the two scripts
 * actually have in common.
 */

/** Collapses the keyboard variants so the exact spelling used months ago stops mattering. */
export function searchKey(input: string): string {
  return input
    .toLowerCase()
    .replace(/[ﻱﻲﻳﻴيی]/g, 'ی')
    .replace(/[كک]/g, 'ک')
    .replace(/[ہھﮦﮧﮨﮩه]/g, 'ہ')
    .replace(/[آأإا]/g, 'ا')
    .replace(/[ؤو]/g, 'و')
    .replace(/[ۃة]/g, 'ہ')
    .replace(/[ً-ٰٟ]/g, '') // harakat
    .replace(/\s+/g, ' ')
    .trim()
}

const URDU_TO_LATIN: Record<string, string> = {
  ا: '', آ: '', ب: 'b', پ: 'p', ت: 't', ٹ: 't', ث: 's', ج: 'j', چ: 'c',
  ح: 'h', خ: 'k', د: 'd', ڈ: 'd', ذ: 'z', ر: 'r', ڑ: 'r', ز: 'z', ژ: 'z',
  س: 's', ش: 's', ص: 's', ض: 'z', ط: 't', ظ: 'z', ع: '', غ: 'g', ف: 'f',
  ق: 'k', ک: 'k', گ: 'g', ل: 'l', م: 'm', ن: 'n', ں: 'n', و: '', ہ: '',
  ھ: '', ء: '', ی: '', ے: '',
}

/**
 * Reduces a name in either script to its bare consonants, so the two scripts
 * can be compared at all. Vowels go, h/w/y go, doubled letters collapse:
 * "محمد اسلم" and "muhammad aslam" both end up as "mhmdslm".
 */
export function nameSkeleton(input: string): string {
  const normalised = searchKey(input)

  /*
   * The Latin side has to be spelled the way the table above spells things, or
   * the two halves never meet. The table writes ق as k and چ as c, while a
   * person typing the same name writes "rafiq" and "chacha" - so رفیق چاچا and
   * "rafiq chacha" came out as "rfk c" against "rfq cc" and did not match at
   * all, which is the one case this whole function exists for.
   */
  const spelled = normalised.replace(/ch/g, 'c').replace(/q/g, 'k')

  let latin = ''
  for (const ch of spelled) {
    if (ch in URDU_TO_LATIN) latin += URDU_TO_LATIN[ch]
    else if (/[a-z]/.test(ch)) latin += ch
    else if (/\s/.test(ch)) latin += ' '
  }

  return latin
    .replace(/[aeiouhwy]/g, '')
    .replace(/(.)\1+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Whether there is any Urdu letter in this text, which is what picks the script. */
export function hasUrdu(input: string): boolean {
  return /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(input)
}

/**
 * How well a name answers what was typed, 6 down to 0.
 *
 * Same-script hits always outrank cross-script guesses, because a cross-script
 * match is a clever inference and a same-script match is just the right answer.
 *
 * Matching any WORD of the name matters as much as matching the start: a great
 * many customers are "محمد something", and a list where typing three letters
 * returns forty Muhammads is a list nobody can use.
 */
export function scoreName(name: string, query: string): number {
  if (!query.trim()) return 1

  const nameKey = searchKey(name)
  const queryKey = searchKey(query)
  const words = nameKey.split(' ')

  if (nameKey === queryKey) return 6
  if (nameKey.startsWith(queryKey)) return 5
  if (words.some((word) => word.startsWith(queryKey))) return 4
  if (nameKey.includes(queryKey)) return 3

  /*
   * Nothing in the same script. The skeleton pass exists to bridge Urdu and
   * Latin - and ONLY that, which has to be checked before using it.
   *
   * Run between two names in the same script it is not a clever fallback, it is
   * noise, because stripping the vowels out of both sides throws away most of
   * what made them different. Typing "hass" leaves the single consonant "s",
   * and "s" appears somewhere in nearly every name in the book: the search
   * answered four real Hassans followed by a hundred and forty strangers, which
   * is indistinguishable from a search that does not work.
   */
  if (hasUrdu(name) === hasUrdu(query)) return 0

  const nameBones = nameSkeleton(name)
  const queryBones = nameSkeleton(query)
  if (!queryBones) return 0

  if (nameBones.startsWith(queryBones)) return 2
  if (nameBones.split(' ').some((word) => word.startsWith(queryBones))) return 2
  if (nameBones.includes(queryBones)) return 1

  return 0
}

export interface Named {
  id: string
  name: string
}

/** The list to actually show, best answers first. */
export function searchNames<T extends Named>(items: T[], query: string): T[] {
  if (!query.trim()) return items

  return items
    .map((item) => ({ item, score: scoreName(item.name, query) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
    .map((row) => row.item)
}
